import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { documentTranslationListApi } from 'models/localization/documentTranslation/Model.DocumentTranslation';
import ProductEnhancementDocument from 'models/productManagement/productEnhancementDocument/ProductEnhancementDocument';
import { useState } from 'react';
import { productEnhancementDocumentIndexRoute } from './ProductEnhancementDocument.Index';

export enum ProductEnhancementDocumentTabs {
  DEALERS = 'dealers',
  DEALER_GROUPS = 'dealerGroups',
}
export type VerifyProductEnhancementDocumentTab = Utils.VerifyExtends<
  Module.ProductManagement.ProductEnhancementDocument.Tabs,
  Utils.ValueOf<typeof ProductEnhancementDocumentTabs>
>;
export default function useProductEnhancementDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductEnhancementDocument.Params,
    Module.ProductManagement.ProductEnhancementDocument.Query
  >({
    route: productEnhancementDocumentIndexRoute,
  });
  const documentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const [selectedTab, setSelectedTab] = useState<ProductEnhancementDocumentTabs>(
    ProductEnhancementDocumentTabs.DEALERS
  );

  const { payload: currentDocument, isExecuting: isLoadingProductEnhancementDocument } = useApi(
    {
      action: ProductEnhancementDocument.find,
      body: { id: documentId },
      default: null,
      wait: !documentId,
    },
    [documentId]
  );

  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductEnhancementDocument', documentId: [documentId] },
      wait: !documentId,
    },
    [documentId]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const canView = permissions.EDIT_PRODUCT;

  return {
    canView,
    pageRouter,
    selectedTab,
    setSelectedTab,
    productEnhancementDocument: currentDocument,
    isLoadingProductEnhancementDocument,
    permissions,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    views: {
      [ProductEnhancementDocumentTabs.DEALERS]: selectedTab === ProductEnhancementDocumentTabs.DEALERS,
      [ProductEnhancementDocumentTabs.DEALER_GROUPS]: selectedTab === ProductEnhancementDocumentTabs.DEALER_GROUPS,
    },
  };
}

export function useProductEnhancementDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductEnhancementDocument>>('productEnhancementDocumentHook');
}
